import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { AllFees, Amount, SelectedFeeOption, SelectedFeeOptions } from '@/src/types/costAndFees.type';
import type { CostTotals } from '@/src/types/costAndFees.type';
import { SpecialOfferCard } from '../SpecialOfferCard';
import { CostSummaryCard } from '../CostSummaryCard';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import { extractFeeGroups, getAmountMin, hasFeesInSection, isBaseRentFee } from '../costFeeUtils';
import { StructuredFeeGroup } from '../FeeGroup';
import { calculateEstimateAmounts } from '../feeCalculation';
import { estimateContainer } from './estimateCostSection.styles';

type EstimateCostSectionProps = {
  effectiveCosts: CostTotals | null | undefined;
  effectiveFees: AllFees | null | undefined;
  specialOfferText?: string | null;
  hasSpecialOffers?: boolean;
  collapseFeeBreakdown?: boolean;
  showSpecialOffer?: boolean;
  showSummary?: boolean;
  selectedFeeOptions?: SelectedFeeOptions;
  onSelectedFeeOptionChange?: (feeKey: string, selection: SelectedFeeOption | null) => void;
};

export function EstimateCostSection({
  effectiveCosts,
  effectiveFees,
  specialOfferText,
  hasSpecialOffers,
  collapseFeeBreakdown = false,
  showSpecialOffer = true,
  showSummary = true,
  selectedFeeOptions: controlledSelectedFeeOptions,
  onSelectedFeeOptionChange,
}: EstimateCostSectionProps) {
  const t = useTranslations('hdp');
  const [internalSelectedFeeOptions, setInternalSelectedFeeOptions] = useState<SelectedFeeOptions>({});
  const selectedFeeOptions = controlledSelectedFeeOptions ?? internalSelectedFeeOptions;
  const updateSelectedFeeOption = useCallback(
    (feeKey: string, selection: SelectedFeeOption | null) => {
      if (onSelectedFeeOptionChange) {
        onSelectedFeeOptionChange(feeKey, selection);
        return;
      }
      setInternalSelectedFeeOptions((previous) => {
        const next = { ...previous };
        if (selection) next[feeKey] = selection;
        else delete next[feeKey];
        return next;
      });
    },
    [onSelectedFeeOptionChange],
  );

  const monthlySection = effectiveFees?.monthly;
  const moveInSection = effectiveFees?.moveIn;

  const monthlyGroups = monthlySection ? extractFeeGroups(monthlySection) : { required: [], optional: [] };
  const moveInGroups = moveInSection ? extractFeeGroups(moveInSection) : { required: [], optional: [] };

  // Override base rent in the fee list with the selector's specific base rent
  const unitRent = effectiveCosts?.baseRent?.amount;
  const monthlyRequired = monthlyGroups.required.map((fee) => {
    if (isBaseRentFee(fee) && unitRent && !('displayText' in unitRent)) {
      return { ...fee, amount: { min: unitRent.min, max: unitRent.max ?? unitRent.min } as Amount };
    }
    return fee;
  });

  const { monthly: totalMonthly, moveIn: totalMoveIn } = calculateEstimateAmounts(
    effectiveCosts,
    effectiveFees,
    selectedFeeOptions,
  );

  const hasCosts =
    ('displayText' in totalMonthly && totalMonthly.displayText) ||
    ('displayText' in totalMoveIn && totalMoveIn.displayText) ||
    getAmountMin(totalMonthly) > 0 ||
    getAmountMin(totalMoveIn) > 0;

  const hasMonthlyFees = hasFeesInSection(monthlySection);
  const hasMoveInFees = hasFeesInSection(moveInSection);
  const hasVisibleSpecialOffer = showSpecialOffer && hasSpecialOffers && specialOfferText;
  const hasVisibleSummary = showSummary && hasCosts;

  if (!hasVisibleSummary && !hasVisibleSpecialOffer && !hasMonthlyFees && !hasMoveInFees) {
    return null;
  }

  const feeBreakdown = (
    <>
      {hasMonthlyFees && (
        <StructuredFeeGroup
          id="monthly"
          title={t('monthlyRentFees')}
          totalLabel={t('monthlyTotalLabel')}
          required={monthlyRequired}
          optional={monthlyGroups.optional}
          frequency="monthly"
          selectedFeeOptions={selectedFeeOptions}
          onSelectedFeeOptionChange={updateSelectedFeeOption}
        />
      )}

      {hasMoveInFees && (
        <StructuredFeeGroup
          id="one-time"
          title={t('oneTimeFeesTitle')}
          totalLabel={t('oneTimeTotalLabel')}
          required={moveInGroups.required}
          optional={moveInGroups.optional}
          frequency="moveIn"
          selectedFeeOptions={selectedFeeOptions}
          onSelectedFeeOptionChange={updateSelectedFeeOption}
        />
      )}
    </>
  );

  return (
    <div className={estimateContainer}>
      {hasVisibleSpecialOffer && <SpecialOfferCard description={specialOfferText} />}

      {hasVisibleSummary && (
        <CostSummaryCard costs={{ monthly: { amount: totalMonthly }, moveIn: { amount: totalMoveIn } }} />
      )}

      {(hasMonthlyFees || hasMoveInFees) &&
        (collapseFeeBreakdown ? (
          <ShowMoreContent
            controlsId="cost-and-fees-module-breakdown"
            showMoreLabel={t('showMore')}
            showLessLabel={t('showLess')}
            showMoreAriaLabel={`${t('showMore')}: ${t('costAndFeesTitle')}`}
            showLessAriaLabel={`${t('showLess')}: ${t('costAndFeesTitle')}`}
          >
            {feeBreakdown}
          </ShowMoreContent>
        ) : (
          feeBreakdown
        ))}
    </div>
  );
}
